import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { PlusIcon, PencilIcon, TrashIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface CustomerSegment {
  id: string;
  name: string;
  description: string;
  painPointsCount: number;
  contactsCount: number;
  status: "active" | "inactive";
}

export interface CustomerSegmentsTabProps {
  /**
   * List of customer segments
   */
  segments?: CustomerSegment[];

  /**
   * Add segment callback
   */
  onAdd?: (segment: Omit<CustomerSegment, "id">) => void;

  /**
   * Edit segment callback
   */
  onEdit?: (id: string, segment: Omit<CustomerSegment, "id">) => void;

  /**
   * Delete segment callback
   */
  onDelete?: (id: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export function CustomerSegmentsTab({
  segments = [],
  onAdd,
  onEdit,
  onDelete,
  className,
}: CustomerSegmentsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    painPointsCount: 0,
    contactsCount: 0,
    status: "active" as "active" | "inactive",
  });

  const handleOpenDialog = (segment?: CustomerSegment) => {
    if (segment) {
      setEditingId(segment.id);
      setFormData({
        name: segment.name,
        description: segment.description,
        painPointsCount: segment.painPointsCount,
        contactsCount: segment.contactsCount,
        status: segment.status,
      });
    } else {
      setEditingId(null);
      setFormData({
        name: "",
        description: "",
        painPointsCount: 0,
        contactsCount: 0,
        status: "active",
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      onEdit?.(editingId, formData);
    } else {
      onAdd?.(formData);
    }
    setIsDialogOpen(false);
  };

  return (
    <div className={className}>
      {/* Add Button */}
      <div className="flex justify-end mb-4">
        <AcademicianStartupButton
          variant="primary"
          leftIcon={<PlusIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Customer
        </AcademicianStartupButton>
      </div>

      {/* Table */}
      <p className="text-sm font-semibold text-primary italic mb-2">For Customer Discovery and Validation Purposes</p>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[25%]">Customer Name</TableHead>
              <TableHead className="w-[35%]">Description</TableHead>
              <TableHead className="w-[12%]">Pain Points</TableHead>
              <TableHead className="w-[12%]">Contacts</TableHead>
              <TableHead className="w-[10%]">Status</TableHead>
              <TableHead className="w-[6%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-8 text-muted-foreground"
                >
                  No customers yet. Click "Add Customer" to get started.
                </TableCell>
              </TableRow>
            ) : (
              segments.map((segment) => (
                <TableRow key={segment.id}>
                  <TableCell className="font-medium">{segment.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {segment.description}
                  </TableCell>
                  <TableCell className="text-center">
                    {segment.painPointsCount}
                  </TableCell>
                  <TableCell className="text-center">
                    {segment.contactsCount}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        segment.status === "active" ? "default" : "secondary"
                      }
                      className={
                        segment.status === "active"
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700"
                          : ""
                      }
                    >
                      {segment.status === "active" ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <AcademicianStartupButton
                        variant="ghost"
                        size="sm"
                        leftIcon={<PencilIcon />}
                        onClick={() => handleOpenDialog(segment)}
                        className="px-2"
                      />

                      <AcademicianStartupButton
                        variant="ghost"
                        size="sm"
                        leftIcon={<TrashIcon />}
                        onClick={() => onDelete?.(segment.id)}
                        className="px-2 text-destructive hover:text-destructive"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the customer details below."
                : "Add a new customer to track."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Customer Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Engineering Managers"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe this customer"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    status: value as "active" | "inactive",
                  })
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <AcademicianStartupButton
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              variant="primary"
              onClick={handleSave}
              disabled={!formData.name || !formData.description}
            >
              {editingId ? "Save Changes" : "Add Customer"}
            </AcademicianStartupButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
